import { Box, Stack } from '@mui/material';
import {
  DATE_FORMAT_MONTH_FNS,
  DEFAULT_DATE_FORMAT_FNS,
  useGetAdmissionAggregateData,
} from '@schooly/api';
import { AdmissionArrangeBy } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { AdmissionDashboardCharts } from '@schooly/components/charts';
import { TabsWithCounters } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { eachMonthOfInterval, format, getYear, isAfter, subYears } from 'date-fns';
import { useCallback, useMemo } from 'react';

import useSchoolYears from '../../hooks/useSchoolYears';
import { DashboardConversionRates } from './DashboardConversionRates';
import { DashboardFilters, getQuarterForDate } from './DashboardFilters';
import { DashboardStatistics } from './DashboardStatistics';

const initialDate = newDateTimezoneOffset();
const initialQuarter = getQuarterForDate(initialDate);
const initialCompareDate = subYears(initialDate, 1);
const initialCompareQuarter = getQuarterForDate(initialCompareDate);
const initialCompareParams = {
  date_from: format(initialQuarter.startDate, DEFAULT_DATE_FORMAT_FNS),
  date_to: format(initialQuarter.endDate, DEFAULT_DATE_FORMAT_FNS),
  reference_date_from: format(initialCompareQuarter.startDate, DEFAULT_DATE_FORMAT_FNS),
  reference_date_to: format(initialCompareQuarter.endDate, DEFAULT_DATE_FORMAT_FNS),
};

export const DashboardContent = () => {
  const { schoolYears, defaultValidity } = useSchoolYears();
  const { schoolId } = useAuth();

  const { data, isLoading, setParams, params, isFetching } = useGetAdmissionAggregateData(
    {
      school_id: schoolId || '',
      arrange_by: AdmissionArrangeBy.Month,
      ...initialCompareParams,
    },
    { refetchOnMount: 'always' },
  );

  const handleStatusChange = useCallback(
    (arrangeBy: AdmissionArrangeBy) => {
      setParams((p) => ({ ...p, arrange_by: arrangeBy }));
    },
    [setParams],
  );

  const { years, currentQuarter, previousYearNumber, currentDate, selectedMonths } = useMemo(() => {
    const baseDate = newDateTimezoneOffset(params.date_from);
    return {
      years: Array.from(
        new Set(
          schoolYears
            .filter(
              (year) =>
                !isAfter(
                  newDateTimezoneOffset(year.end),
                  newDateTimezoneOffset(defaultValidity?.end),
                ),
            )
            .flatMap((year) => [
              getYear(newDateTimezoneOffset(year.start)),
              getYear(newDateTimezoneOffset(year.end)),
            ]),
        ),
      ),

      currentQuarter: getQuarterForDate(baseDate),
      previousYearNumber: getYear(subYears(baseDate, 1)),
      currentDate: baseDate,
      selectedMonths: eachMonthOfInterval({
        start: baseDate,
        end: newDateTimezoneOffset(params.date_to),
      }).map((month) => format(month, DATE_FORMAT_MONTH_FNS)),
    };
  }, [params.date_from, params.date_to, schoolYears, defaultValidity?.end]);

  const handleDateChange = useCallback(
    (date: [Date, Date], compareDate: [Date, Date]) => {
      const [dateFrom, dateTo] = date;
      const [compareDateFrom, compareDateTo] = compareDate;
      setParams((p) => ({
        ...p,
        date_from: format(dateFrom, DEFAULT_DATE_FORMAT_FNS),
        date_to: format(dateTo, DEFAULT_DATE_FORMAT_FNS),
        reference_date_from: format(compareDateFrom, DEFAULT_DATE_FORMAT_FNS),
        reference_date_to: format(compareDateTo, DEFAULT_DATE_FORMAT_FNS),
      }));
    },
    [setParams],
  );

  return (
    <Stack gap={1}>
      <DashboardFilters
        onDateChange={handleDateChange}
        years={years.map((year) => ({ name: year.toString(), value: year.toString() }))}
        currentQuarter={currentQuarter}
        previousYearNumber={previousYearNumber}
        currentDate={currentDate}
      />

      <TabsWithCounters
        tabItems={[
          { labelId: 'dashboard-ArrangeBy-Month', value: AdmissionArrangeBy.Month },
          { labelId: 'dashboard-ArrangeBy-Metric', value: AdmissionArrangeBy.Metric },
        ]}
        onChange={(_, v: AdmissionArrangeBy) => handleStatusChange(v)}
        activeTab={params.arrange_by}
        sx={{
          pt: 1.5,
          '& .MuiTabs-flexContainer': {
            overflow: 'scroll',
          },
          '& .MuiTab-root': {
            height: 24,
            minHeight: 0,
            '&:not(:last-child)': { mr: 4 },
          },
        }}
      />

      <Box sx={{ pt: 1.5 }}>
        <DashboardStatistics
          total={data?.total || []}
          isLoading={isLoading || isFetching}
          compareLabel={`${currentQuarter.name} ${previousYearNumber}`}
        />
      </Box>
      <Stack direction="row" gap={1.5} pt={1.5}>
        <Box sx={{ width: params.arrange_by === 'metric' ? '50%' : '100%' }}>
          <AdmissionDashboardCharts
            data={data?.rows || []}
            arrangeBy={params.arrange_by}
            isLoading={isFetching}
            selectedMonths={selectedMonths}
            currentMetrics={data?.total.map((total) => total.name) || []}
          />
        </Box>
        {params.arrange_by === 'metric' && (
          <DashboardConversionRates
            isLoading={isLoading || isFetching}
            total={data?.total || []}
            compareLabel={`${currentQuarter.name} ${previousYearNumber}`}
          />
        )}
      </Stack>
    </Stack>
  );
};
